var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconClipboardList, IconFolder, IconUmbrella, } from "@tabler/icons-react";
import { icons, typeAlert, } from "@affinitysystemsgroup/github-packages-affinity-components/dist";
import LocalStorageService from "../../../services/localStorage";
import { showErrorToast } from "../../../fnx/showError";
import { usePropertyEntitiesGetAll } from "../../../fnx/properties/propertyEntitiesGetAll";
import { usePropertiesGetAll } from "../../../fnx/properties/propertiesGetAll";
import { usePropertiesMutations } from "../../../fnx/properties/propertiesMutations";
import { useGetPlansAllV2 } from "../../../fnx/plans/planGetAllV2";
import { getEntityLabel, PROPERTIES_CAN_CREATE, PROPERTIES_CAN_DELETE, PROPERTIES_CAN_EDIT, PROPERTIES_CLIENT_ENTITY_NAME, PROPERTIES_COVER_TYPE_ENTITY_NAME, PROPERTIES_DEFAULT_LOADING_TEXT, PROPERTIES_GLOBAL_FILTER_ALL_LABEL, PROPERTIES_INITIAL_LOADING_TEXT, PROPERTIES_MUTATION_LOADING_TEXT, PROPERTIES_MUTATION_SUCCESS_MESSAGES, PROPERTIES_PAGE_DESCRIPTION, PROPERTIES_PAGE_TITLE, PROPERTIES_PLAN_ENTITY_NAME, PROPERTIES_PROJECT_ENTITY_NAME, PROPERTY_ENTITIES, } from "../constants/properties.constants";
var storageKey = "assurehub";
var lsLogin = new LocalStorageService(storageKey);
var PLAN_LOADING_NODE_SUFFIX = "__loading-plans";
var entityRecordLabel = function (record) { var _a, _b, _c; return (_c = (_b = (_a = record.description) !== null && _a !== void 0 ? _a : record.name) !== null && _b !== void 0 ? _b : record.code) !== null && _c !== void 0 ? _c : record.id; };
var planLabel = function (plan) { var _a, _b, _c; return (_c = (_b = (_a = plan.name) !== null && _a !== void 0 ? _a : plan.description) !== null && _b !== void 0 ? _b : plan.code) !== null && _c !== void 0 ? _c : plan.id; };
var byLabel = function (items, getLabel) {
    return __spreadArray([], items, true).sort(function (a, b) {
        return getLabel(a).localeCompare(getLabel(b), "es", { sensitivity: "base" });
    });
};
var countByEntityId = function (rows) {
    var counts = {};
    rows.forEach(function (row) {
        var _a;
        if (!row.entityId)
            return;
        counts[row.entityId] = ((_a = counts[row.entityId]) !== null && _a !== void 0 ? _a : 0) + 1;
    });
    return counts;
};
var buildProjectTree = function (params) {
    var planChildrenByProject = params.planChildrenByProject, countsByEntityId = params.countsByEntityId;
    var projectRecords = byLabel(params.projectRecords, entityRecordLabel);
    var ancestry = {};
    var nodes = projectRecords.map(function (project) {
        var _a;
        var projectLabel = entityRecordLabel(project);
        ancestry[project.id] = [projectLabel];
        var plansUnsorted = planChildrenByProject[project.id];
        var children;
        if (plansUnsorted === undefined) {
            children = [
                {
                    id: "".concat(project.id).concat(PLAN_LOADING_NODE_SUFFIX),
                    label: "Cargando planes...",
                },
            ];
        }
        else {
            var plans = byLabel(plansUnsorted, planLabel);
            children = plans.map(function (plan) {
                var _a, _b;
                var label = planLabel(plan);
                ancestry[plan.id] = [projectLabel, label];
                var coverTypeNodes = byLabel((_a = plan.coverTypes) !== null && _a !== void 0 ? _a : [], function (coverType) { var _a, _b; return (_b = (_a = coverType.description) !== null && _a !== void 0 ? _a : coverType.code) !== null && _b !== void 0 ? _b : coverType.id; }).map(function (coverType) {
                    var _a, _b, _c;
                    var coverTypeLabel = (_b = (_a = coverType.description) !== null && _a !== void 0 ? _a : coverType.code) !== null && _b !== void 0 ? _b : coverType.id;
                    ancestry[coverType.id] = [projectLabel, label, coverTypeLabel];
                    return {
                        id: coverType.id,
                        label: coverTypeLabel,
                        icon: _jsx(IconUmbrella, { size: 13 }),
                        meta: PROPERTIES_COVER_TYPE_ENTITY_NAME,
                        count: (_c = countsByEntityId[coverType.id]) !== null && _c !== void 0 ? _c : 0,
                    };
                });
                return {
                    id: plan.id,
                    label: label,
                    icon: _jsx(IconClipboardList, { size: 13 }),
                    meta: PROPERTIES_PLAN_ENTITY_NAME,
                    count: (_b = countsByEntityId[plan.id]) !== null && _b !== void 0 ? _b : 0,
                    children: coverTypeNodes.length ? coverTypeNodes : undefined,
                };
            });
        }
        return {
            id: project.id,
            label: projectLabel,
            icon: _jsx(IconFolder, { size: 13 }),
            meta: PROPERTIES_PROJECT_ENTITY_NAME,
            count: (_a = countsByEntityId[project.id]) !== null && _a !== void 0 ? _a : 0,
            children: children,
        };
    });
    return { nodes: nodes, ancestry: ancestry };
};
var buildFlatTree = function (params) {
    var countsByEntityId = params.countsByEntityId, entityName = params.entityName;
    var records = byLabel(params.records, entityRecordLabel);
    var ancestry = {};
    var nodes = records.map(function (record) {
        var _a;
        var label = entityRecordLabel(record);
        ancestry[record.id] = [label];
        return {
            id: record.id,
            label: label,
            meta: entityName,
            count: (_a = countsByEntityId[record.id]) !== null && _a !== void 0 ? _a : 0,
        };
    });
    return { nodes: nodes, ancestry: ancestry };
};
var nodeMatchesQuery = function (node, query) {
    var _a;
    if (node.label.toLowerCase().includes(query))
        return true;
    return ((_a = node.children) !== null && _a !== void 0 ? _a : []).some(function (child) { return nodeMatchesQuery(child, query); });
};
var filterTreeNodes = function (nodes, query) {
    if (!query)
        return nodes;
    return nodes
        .filter(function (node) { return nodeMatchesQuery(node, query); })
        .map(function (node) { return (__assign(__assign({}, node), { children: node.children
            ? filterTreeNodes(node.children, query)
            : node.children })); });
};
var collectExpandableIds = function (nodes) {
    var ids = [];
    nodes.forEach(function (node) {
        if (node.children && node.children.length > 0) {
            ids.push(node.id);
            ids.push.apply(ids, collectExpandableIds(node.children));
        }
    });
    return ids;
};
export var useProperties = function () {
    var _a, _b;
    var t = useTranslation().t;
    var clientId = useState(((_a = lsLogin.getUserLoginStored()) === null || _a === void 0 ? void 0 : _a.clientId) || "")[0];
    var clientName = useState(((_b = lsLogin.getUserLoginStored()) === null || _b === void 0 ? void 0 : _b.clientName) || "")[0];
    var _c = useState(false), trigger = _c[0], setTrigger = _c[1];
    var _d = useState(PROPERTY_ENTITIES[0].entityName), selectedCategory = _d[0], setSelectedCategory = _d[1];
    var _e = useState([]), expandedIds = _e[0], setExpandedIds = _e[1];
    var _f = useState(""), recordSearch = _f[0], setRecordSearch = _f[1];
    var _g = useState(null), selectedRecord = _g[0], setSelectedRecord = _g[1];
    var _h = useState(""), globalEntityFilter = _h[0], setGlobalEntityFilter = _h[1];
    var _j = useState(null), editingRecordProperty = _j[0], setEditingRecordProperty = _j[1];
    var _k = useState({}), planChildrenByProject = _k[0], setPlanChildrenByProject = _k[1];
    var _l = useState(undefined), pendingProjectId = _l[0], setPendingProjectId = _l[1];
    var sawPlansLoadingRef = useRef(false);
    var _m = useState({
        show: false,
        message: "",
        type: typeAlert.success,
    }), seeToast = _m[0], setSeeToast = _m[1];
    var _o = usePropertyEntitiesGetAll(trigger, PROPERTY_ENTITIES, clientId), responsePropertyEntities = _o.responsePropertyEntities, errorPropertyEntities = _o.errorPropertyEntities, loadingPropertyEntities = _o.loadingPropertyEntities;
    var clientCategory = useMemo(function () { return ({
        entityName: PROPERTIES_CLIENT_ENTITY_NAME,
        label: getEntityLabel(PROPERTIES_CLIENT_ENTITY_NAME),
        icon: icons.AccountCircle,
        records: clientId
            ? [{ id: clientId, description: clientName || clientId }]
            : [],
    }); }, [clientId, clientName]);
    var allPropertyEntities = useMemo(function () { return __spreadArray(__spreadArray([], responsePropertyEntities, true), [clientCategory], false); }, [responsePropertyEntities, clientCategory]);
    var _p = usePropertiesGetAll(), responseProperties = _p.responseProperties, loadingProperties = _p.loadingProperties, getAllProperties = _p.getAllProperties;
    var normalizeEntityName = function (entity) {
        return entity.trim().toLowerCase();
    };
    var propertiesByEntity = useMemo(function () {
        var map = {};
        responseProperties.forEach(function (property) {
            var _a;
            var entity = normalizeEntityName((_a = property.entityName) !== null && _a !== void 0 ? _a : "");
            if (!map[entity])
                map[entity] = [];
            map[entity].push(property);
        });
        return map;
    }, [responseProperties]);
    var getPropsByEntity = function (entity) { var _a; return (_a = propertiesByEntity[normalizeEntityName(entity)]) !== null && _a !== void 0 ? _a : []; };
    var _q = usePropertiesMutations(), createProperty = _q.createProperty, updateProperty = _q.updateProperty, clearPropertyMutation = _q.clearPropertyMutation, loadingPropertyMutation = _q.loadingPropertyMutation, responsePropertyMutation = _q.responsePropertyMutation, errorPropertyMutation = _q.errorPropertyMutation;
    var _r = useGetPlansAllV2(Boolean(pendingProjectId), undefined, pendingProjectId), responsePlans = _r.responsePlans, loadingPlans = _r.loadingPlans, errorPlans = _r.errorPlans;
    useEffect(function () {
        setTrigger(true);
    }, []);
    var projectRecords = useMemo(function () {
        var _a, _b;
        return (_b = (_a = responsePropertyEntities.find(function (category) { return category.entityName === PROPERTIES_PROJECT_ENTITY_NAME; })) === null || _a === void 0 ? void 0 : _a.records) !== null && _b !== void 0 ? _b : [];
    }, [responsePropertyEntities]);
    useEffect(function () {
        if (selectedCategory !== PROPERTIES_PROJECT_ENTITY_NAME)
            return;
        if (pendingProjectId)
            return;
        var nextProjectId = expandedIds.find(function (id) {
            return planChildrenByProject[id] === undefined &&
                projectRecords.some(function (project) { return project.id === id; });
        });
        if (nextProjectId) {
            sawPlansLoadingRef.current = false;
            setPendingProjectId(nextProjectId);
        }
    }, [
        expandedIds,
        planChildrenByProject,
        selectedCategory,
        projectRecords,
        pendingProjectId,
    ]);
    useEffect(function () {
        if (loadingPlans)
            sawPlansLoadingRef.current = true;
    }, [loadingPlans]);
    useEffect(function () {
        if (!pendingProjectId)
            return;
        if (loadingPlans)
            return;
        if (!sawPlansLoadingRef.current)
            return;
        if (responsePlans === undefined)
            return;
        setPlanChildrenByProject(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[pendingProjectId] = responsePlans !== null && responsePlans !== void 0 ? responsePlans : [], _a)));
        });
        setPendingProjectId(undefined);
        sawPlansLoadingRef.current = false;
    }, [responsePlans, loadingPlans, pendingProjectId]);
    var countsByEntityId = useMemo(function () {
        if (selectedCategory === PROPERTIES_PROJECT_ENTITY_NAME) {
            return countByEntityId(__spreadArray(__spreadArray(__spreadArray([], getPropsByEntity(PROPERTIES_PROJECT_ENTITY_NAME), true), getPropsByEntity(PROPERTIES_PLAN_ENTITY_NAME), true), getPropsByEntity(PROPERTIES_COVER_TYPE_ENTITY_NAME), true));
        }
        return countByEntityId(getPropsByEntity(selectedCategory !== null && selectedCategory !== void 0 ? selectedCategory : ""));
    }, [selectedCategory, propertiesByEntity]);
    var _s = useMemo(function () {
        var _a, _b;
        if (selectedCategory === PROPERTIES_PROJECT_ENTITY_NAME) {
            return buildProjectTree({
                projectRecords: projectRecords,
                planChildrenByProject: planChildrenByProject,
                countsByEntityId: countsByEntityId,
            });
        }
        var records = (_b = (_a = allPropertyEntities.find(function (category) { return category.entityName === selectedCategory; })) === null || _a === void 0 ? void 0 : _a.records) !== null && _b !== void 0 ? _b : [];
        return buildFlatTree({
            records: records,
            countsByEntityId: countsByEntityId,
            entityName: selectedCategory !== null && selectedCategory !== void 0 ? selectedCategory : "",
        });
    }, [
        selectedCategory,
        projectRecords,
        planChildrenByProject,
        countsByEntityId,
        allPropertyEntities,
    ]), rawTreeNodes = _s.nodes, ancestry = _s.ancestry;
    var globalAncestry = useMemo(function () {
        var merged = {};
        allPropertyEntities.forEach(function (category) {
            var _a;
            if (category.entityName === PROPERTIES_PROJECT_ENTITY_NAME) {
                Object.assign(merged, buildProjectTree({
                    projectRecords: projectRecords,
                    planChildrenByProject: planChildrenByProject,
                    countsByEntityId: {},
                }).ancestry);
                return;
            }
            Object.assign(merged, buildFlatTree({
                records: (_a = category.records) !== null && _a !== void 0 ? _a : [],
                countsByEntityId: {},
                entityName: category.entityName,
            }).ancestry);
        });
        return merged;
    }, [allPropertyEntities, projectRecords, planChildrenByProject]);
    var normalizedSearch = recordSearch.trim().toLowerCase();
    var treeNodes = useMemo(function () { return filterTreeNodes(rawTreeNodes, normalizedSearch); }, [rawTreeNodes, normalizedSearch]);
    var effectiveExpandedIds = normalizedSearch
        ? Array.from(new Set(__spreadArray(__spreadArray([], expandedIds, true), collectExpandableIds(treeNodes), true)))
        : expandedIds;
    var onSelectCategory = function (entityName) {
        setSelectedCategory(entityName);
        setSelectedRecord(null);
        setEditingRecordProperty(null);
        setGlobalEntityFilter("");
        setRecordSearch("");
        setExpandedIds([]);
    };
    var onExpandedIdsChange = function (ids) {
        setExpandedIds(ids);
    };
    var onChangeRecordSearch = function (text) {
        setRecordSearch(text);
    };
    var onSelectTreeNode = function (node) {
        var _a, _b, _c;
        if (node.id.endsWith(PLAN_LOADING_NODE_SUFFIX))
            return;
        if (selectedRecord && selectedRecord.entityId === node.id) {
            setSelectedRecord(null);
            setEditingRecordProperty(null);
            return;
        }
        setSelectedRecord({
            entity: (_b = (_a = node.meta) !== null && _a !== void 0 ? _a : selectedCategory) !== null && _b !== void 0 ? _b : "",
            entityId: node.id,
            label: node.label,
            breadcrumb: (_c = ancestry[node.id]) !== null && _c !== void 0 ? _c : [node.label],
        });
        setEditingRecordProperty(null);
        if (node.children &&
            node.children.length > 0 &&
            !expandedIds.includes(node.id)) {
            setExpandedIds(function (prev) { return __spreadArray(__spreadArray([], prev, true), [node.id], false); });
        }
    };
    var onClearSelectedRecord = function () {
        setSelectedRecord(null);
        setEditingRecordProperty(null);
    };
    var onGoToRecord = function (entity, entityId) {
        var _a, _b, _c;
        setSelectedRecord({
            entity: entity,
            entityId: entityId,
            label: (_b = (_a = globalAncestry[entityId]) === null || _a === void 0 ? void 0 : _a[globalAncestry[entityId].length - 1]) !== null && _b !== void 0 ? _b : entityId,
            breadcrumb: (_c = globalAncestry[entityId]) !== null && _c !== void 0 ? _c : [entityId],
        });
        setEditingRecordProperty(null);
        if (selectedCategory === PROPERTIES_PROJECT_ENTITY_NAME &&
            entity === PROPERTIES_PROJECT_ENTITY_NAME &&
            !expandedIds.includes(entityId)) {
            setExpandedIds(function (prev) { return __spreadArray(__spreadArray([], prev, true), [entityId], false); });
        }
    };
    var globalRows = useMemo(function () {
        return byLabel(byLabel(responseProperties.map(function (property) {
            var _a, _b, _c, _d, _e, _f;
            return ({
                propertyId: property.id,
                entity: (_a = property.entityName) !== null && _a !== void 0 ? _a : "",
                entityId: (_b = property.entityId) !== null && _b !== void 0 ? _b : "",
                recordLabel: (_f = (_e = (_d = globalAncestry[(_c = property.entityId) !== null && _c !== void 0 ? _c : ""]) === null || _d === void 0 ? void 0 : _d.slice(-1)[0]) !== null && _e !== void 0 ? _e : property.entityId) !== null && _f !== void 0 ? _f : "",
                name: property.name,
                value: property.value,
            });
        }), function (row) { return row.name; }), function (row) { return row.recordLabel; });
    }, [responseProperties, globalAncestry]);
    var globalEntityFilterOptions = useMemo(function () {
        var entities = Array.from(new Set(globalRows.map(function (row) { return normalizeEntityName(row.entity); })));
        return entities.length > 1 ? entities : [];
    }, [globalRows]);
    var filteredGlobalRows = globalEntityFilter
        ? globalRows.filter(function (row) { return normalizeEntityName(row.entity) === globalEntityFilter; })
        : globalRows;
    var onChangeGlobalEntityFilter = function (entity) {
        setGlobalEntityFilter(entity === PROPERTIES_GLOBAL_FILTER_ALL_LABEL ? "" : entity);
    };
    var recordProperties = useMemo(function () {
        if (!selectedRecord)
            return [];
        return byLabel(getPropsByEntity(selectedRecord.entity)
            .filter(function (property) { return property.entityId === selectedRecord.entityId; })
            .map(function (property) { return ({
            propertyId: property.id,
            name: property.name,
            value: property.value,
        }); }), function (row) { return row.name; });
    }, [selectedRecord, propertiesByEntity]);
    var onQuickEditProperty = function (row, newName, newValue) {
        if (!row.propertyId)
            return;
        updateProperty({
            id: row.propertyId,
            name: row.name,
            value: row.value,
            entityName: row.entity,
            entityId: row.entityId,
        }, {
            name: newName,
            value: newValue,
            entityName: row.entity,
            entityId: row.entityId,
        });
    };
    var onStartEditRecordProperty = function (row) {
        setEditingRecordProperty(row);
    };
    var onCancelEditRecordProperty = function () {
        setEditingRecordProperty(null);
    };
    var onSubmitRecordProperty = function (name, value) {
        if (!selectedRecord)
            return;
        var payload = {
            name: name,
            value: value,
            entityName: selectedRecord.entity,
            entityId: selectedRecord.entityId,
        };
        if (editingRecordProperty === null || editingRecordProperty === void 0 ? void 0 : editingRecordProperty.propertyId) {
            updateProperty({
                id: editingRecordProperty.propertyId,
                name: editingRecordProperty.name,
                value: editingRecordProperty.value,
                entityName: selectedRecord.entity,
                entityId: selectedRecord.entityId,
            }, payload);
        }
        else {
            createProperty(payload);
        }
        setEditingRecordProperty(null);
    };
    useEffect(function () {
        if (!responsePropertyMutation)
            return;
        setSeeToast({
            show: true,
            message: PROPERTIES_MUTATION_SUCCESS_MESSAGES[responsePropertyMutation],
            type: typeAlert.success,
        });
        getAllProperties();
        clearPropertyMutation();
    }, [responsePropertyMutation]);
    useEffect(function () {
        if (errorPropertyEntities)
            showErrorToast(errorPropertyEntities, setSeeToast, t);
    }, [errorPropertyEntities]);
    useEffect(function () {
        if (errorPropertyMutation) {
            showErrorToast(errorPropertyMutation, setSeeToast, t);
            clearPropertyMutation();
        }
    }, [errorPropertyMutation]);
    useEffect(function () {
        if (errorPlans)
            showErrorToast(errorPlans, setSeeToast, t);
    }, [errorPlans]);
    var categoryOptions = allPropertyEntities.map(function (category) {
        var _a, _b, _c;
        var definition = PROPERTY_ENTITIES.find(function (entity) { return entity.entityName === category.entityName; });
        return {
            entityName: category.entityName,
            label: (_a = category.label) !== null && _a !== void 0 ? _a : getEntityLabel(category.entityName),
            subtitle: "".concat((_c = (_b = category.records) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0, " registro(s)"),
            dot: definition === null || definition === void 0 ? void 0 : definition.noteType,
            note: definition === null || definition === void 0 ? void 0 : definition.note,
            noteType: definition === null || definition === void 0 ? void 0 : definition.noteType,
            selected: selectedCategory === category.entityName,
        };
    });
    var blockingLoading = loadingPropertyEntities || loadingPropertyMutation;
    var textLoading = loadingPropertyMutation
        ? PROPERTIES_MUTATION_LOADING_TEXT
        : PROPERTIES_INITIAL_LOADING_TEXT;
    return {
        blockingLoading: blockingLoading,
        textLoading: textLoading !== null && textLoading !== void 0 ? textLoading : PROPERTIES_DEFAULT_LOADING_TEXT,
        seeToast: seeToast,
        setSeeToast: setSeeToast,
        title: PROPERTIES_PAGE_TITLE,
        description: PROPERTIES_PAGE_DESCRIPTION,
        categoryOptions: categoryOptions,
        selectedCategory: selectedCategory,
        onSelectCategory: onSelectCategory,
        isTreeCategory: selectedCategory === PROPERTIES_PROJECT_ENTITY_NAME,
        treeNodes: treeNodes,
        expandedIds: effectiveExpandedIds,
        onExpandedIdsChange: onExpandedIdsChange,
        recordSearch: recordSearch,
        onChangeRecordSearch: onChangeRecordSearch,
        selectedRecordId: selectedRecord === null || selectedRecord === void 0 ? void 0 : selectedRecord.entityId,
        onSelectTreeNode: onSelectTreeNode,
        recordsLoading: loadingPropertyEntities,
        selectedRecord: selectedRecord,
        onClearSelectedRecord: onClearSelectedRecord,
        onGoToRecord: onGoToRecord,
        globalRows: filteredGlobalRows,
        globalEntityFilterOptions: globalEntityFilterOptions,
        globalEntityFilter: globalEntityFilter || PROPERTIES_GLOBAL_FILTER_ALL_LABEL,
        onChangeGlobalEntityFilter: onChangeGlobalEntityFilter,
        globalLoading: loadingProperties,
        onQuickEditProperty: onQuickEditProperty,
        recordProperties: recordProperties,
        editingRecordProperty: editingRecordProperty,
        onStartEditRecordProperty: onStartEditRecordProperty,
        onCancelEditRecordProperty: onCancelEditRecordProperty,
        onSubmitRecordProperty: onSubmitRecordProperty,
        canCreate: PROPERTIES_CAN_CREATE,
        canEdit: PROPERTIES_CAN_EDIT,
        canDelete: PROPERTIES_CAN_DELETE,
    };
};
